import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  EventEmitter,
  Output,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { PingIrsService } from './ping-irs.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  buildHeaderDataShell,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-ping-irs',
    templateUrl: './ping-irs.component.html',
    styleUrls: ['./ping-irs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PingIrsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private isLoading = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  envType: string = 'TEST';
  pinging: boolean = false;
  minimise: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private gtwSnackbarService: GtwSnackbarService,
    private pingIrsService: PingIrsService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = gc;
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const hdr = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = hdr.userSettingsData;
    this.SERVER_CONFIG = hdr.serverConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    const efileApiUrl = this.baseURLs?.efile || this.GENERAL_CONFIG?.gtw_efile_api_url;
    if (!efileApiUrl) {
      return;
    }
    this.isInitialized = true;
  }

  get efileApiUrl(): string {
    return this.baseURLs?.efile || this.GENERAL_CONFIG?.gtw_efile_api_url || '';
  }

  pingToIRS(): void {
    const efileApiUrl = this.efileApiUrl;
    if (!efileApiUrl) {
      this.gtwSnackbarService.openSnackBar('Efile API URL not configured.', 'gtw-snackbar--red');
      return;
    }

    this.pinging = true;
    this.cdr.detectChanges();

    this.pingIrsService.pingIRS(efileApiUrl, this.envType).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res) => {
        this.pinging = false;
        const callSuccess = res?.callSuccess ?? res?.data?.callSuccess;
        if (callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Successfully Pinged IRS!', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar('Failed to Ping IRS!', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.pinging = false;
        this.gtwSnackbarService.openSnackBar('Failed to Ping IRS!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document.querySelector('gtw-efile-ping-irs-ce')?.dispatchEvent(updateEvent);
  }
}
